import { Fragment, useState } from 'react';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';
import isEmpty from 'lodash/fp/isEmpty';
import { ColumnDetailsMap } from '../../../shared/models/ColumnDetailsMap';
import { ColumnDetails } from '../../../shared/models/ColumnDetails';
import { ColumnLabel } from '../../../shared/models/ColumnLabel';
import NotFoundStateDashboard from '../../../shared/components/NotFoundState';
import TableHeader from '../../../shared/components/TableHeader';
import { IExtractQueryParams } from '../../../shared/models/IExtractQueryParams';


const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
  const style = columnDetails?.width
    ? {
      minWidth: columnDetails.width,
      width: columnDetails.width,
    }
    : {};
  return <col key={column} style={style} />;
};

type DashboardTableBodyProps = {
  searchResult: any[];
  columns: string[];
  columnLabels: ColumnLabel;
  columnsDetails: ColumnDetailsMap;
  subRows: string[];
  tableClassNames: string;
  isLoading: boolean;
  isViewTypeTable: boolean;
  screenWidth: number;
  width: number;
  expandedRows: string[];
  assetId: string | null;
  TableRowContent: React.FC<{
    row: any;
    index: number;
    columns: string[];
    columnLabels: ColumnLabel;
    expandedRows?: string[];
    handleToggleRow?: (id: string) => void;
    currentExtractLinkIndex: any;
    setCurrentExtractLinkIndex: any;
    queryParams: IExtractQueryParams;
  }>;
  ExpandedRowDetails?: React.FC<{
    row: any;
    colSpan: number;
    tableClassNames: string;
    handleShowMap: (lat: number, lng: number) => void;
  }>
  handleToggleRow: (id: string) => void;
  handleShowMap?: (lat: number, lng: number) => void;
};

const DashboardTableBody = (props: DashboardTableBodyProps) => {
  const {
    searchResult,
    columns,
    columnLabels,
    columnsDetails,
    subRows,
    tableClassNames,
    isLoading,
    isViewTypeTable,
    screenWidth,
    expandedRows,
    assetId,
    TableRowContent,
    ExpandedRowDetails,
    handleToggleRow,
    handleShowMap,
  } = props;

  const [currentExtractLinkIndex, setCurrentExtractLinkIndex] = useState<number>();

  const columnsWithoutSubRows = columns.filter(col => !subRows.includes(col));

  if (isEmpty(searchResult) && !isViewTypeTable && !isLoading) {
    return <NotFoundStateDashboard />;
  }

  return (
    <table
      style={{ minWidth: screenWidth < 1734 ? 1734 : undefined }}
      className={tableClassNames}
    >
      <colgroup>
        {columnsWithoutSubRows.map(column => renderTableCaption(column, columnsDetails[column]))}
      </colgroup>

      {!isEmpty(searchResult) && (
        <thead
          style={{
            position: screenWidth < 1734 ? 'sticky' : undefined,
            zIndex: screenWidth < 1734 ? 2 : undefined,
            top: screenWidth < 1734 ? -2 : undefined,
            pointerEvents: 'none',
          }}
        >
          <tr>
            {columnsWithoutSubRows.map(column => (
              <TableHeader
                key={column}
                column={column}
                label={columnLabels[column]}
                dontFilterByColums={[]}
              />
            ))}
          </tr>
        </thead>
      )}

      <tbody>
        {isLoading ? (
          [...Array(4)].map((_, i) => (
            <tr key={i} className='compactRow'>
              {columnsWithoutSubRows.map((col, idx) => (
                <td key={idx} className='text-size-14' data-field={columnLabels[col]}>
                  <span>
                    <ContentLoader />
                  </span>
                </td>
              ))}
            </tr>
          ))
        ) : isEmpty(searchResult) && isViewTypeTable ? (
          <tr>
            <td colSpan={columnsWithoutSubRows.length}>
              <NotFoundStateDashboard />
            </td>
          </tr>
        ) : (
          searchResult.map((row: any, index: number) => {

            const extractParams: IExtractQueryParams = {
              assetId: assetId, year: row.year, month: row.month
            }

            return (
              <Fragment key={index}>
                <TableRowContent
                  row={row}
                  index={index}
                  columns={columns}
                  columnLabels={columnLabels}
                  expandedRows={expandedRows}
                  handleToggleRow={handleToggleRow}
                  currentExtractLinkIndex={currentExtractLinkIndex}
                  setCurrentExtractLinkIndex={setCurrentExtractLinkIndex}
                  queryParams={extractParams}
                />
                {
                  ExpandedRowDetails && handleShowMap && expandedRows.includes(`${index}`) && (
                    <ExpandedRowDetails
                      row={row}
                      colSpan={columnsWithoutSubRows.length}
                      tableClassNames={tableClassNames}
                      handleShowMap={handleShowMap}
                    />
                  )
                }
              </Fragment>
            )
          })
        )}
        {[...Array(10)].map((_, i) => (
          <tr key={`placeholder-${i}`} className='table-card-placeholder' />
        ))}
      </tbody>
    </table>
  );
};

export default DashboardTableBody;